import { useState } from 'react';
import {
  Badge,
  Button,
  Card,
  DataTable,
  EmptyState,
  Field,
  Modal,
  Select,
  Skeleton,
  TextArea,
  TextInput,
  useToast,
} from '@api-hub/ui';
import { PageHeader } from '../components/Layout';
import { api, describeError } from '../lib/api';
import { useResource } from '../lib/hooks';
import { toneForLifecycle } from '../lib/format';

interface Service {
  id: string;
  serviceKey: string;
  name: string;
  summary: string;
  description: string | null;
  state: string;
  sensitivity: string;
  tags: string[];
  _count: { operations: number; knowledgeSources: number };
}

export function ServicesPage({ role }: { role: string }) {
  const services = useResource<{ items: Service[] }>('/api/admin/services');
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    serviceKey: '',
    name: '',
    summary: '',
    description: '',
    sensitivity: 'INTERNAL',
  });
  const canWrite = role === 'OWNER' || role === 'ADMIN';

  return (
    <>
      <PageHeader
        title="Katalog usług"
        description="Usługa grupuje operacje i źródła wiedzy. To jej opis agent zobaczy jako pierwszy podczas wyszukiwania."
        actions={
          canWrite ? (
            <Button variant="primary" onClick={() => setOpen(true)}>
              Dodaj usługę
            </Button>
          ) : undefined
        }
      />

      <Card tight>
        {services.loading ? (
          <div className="ah-card-body">
            <Skeleton rows={4} />
          </div>
        ) : (
          <DataTable
            rows={services.data?.items ?? []}
            rowKey={(row) => row.id}
            empty={
              <EmptyState
                title="Brak usług"
                description="Usługa jest wymagana, zanim utworzysz pierwszą operację."
              />
            }
            columns={[
              {
                key: 'key',
                header: 'Klucz',
                render: (row) => <span className="ah-mono">{row.serviceKey}</span>,
              },
              {
                key: 'name',
                header: 'Nazwa',
                render: (row) => (
                  <>
                    <div>{row.name}</div>
                    <div style={{ fontSize: 12, color: 'var(--ah-text-muted)' }}>{row.summary}</div>
                  </>
                ),
              },
              {
                key: 'state',
                header: 'Cykl życia',
                render: (row) => <Badge tone={toneForLifecycle(row.state)}>{row.state}</Badge>,
              },
              {
                key: 'sensitivity',
                header: 'Wrażliwość',
                render: (row) => (
                  <Badge tone={row.sensitivity === 'RESTRICTED' ? 'danger' : 'neutral'}>
                    {row.sensitivity}
                  </Badge>
                ),
              },
              {
                key: 'operations',
                header: 'Operacje',
                align: 'right',
                render: (row) => row._count.operations,
              },
              {
                key: 'sources',
                header: 'Źródła wiedzy',
                align: 'right',
                render: (row) => row._count.knowledgeSources,
              },
              {
                key: 'actions',
                header: '',
                align: 'right',
                render: (row) =>
                  canWrite ? (
                    <Button
                      size="sm"
                      onClick={async () => {
                        try {
                          await api.patch(`/api/admin/services/${row.id}`, {
                            state: row.state === 'PUBLISHED' ? 'DEPRECATED' : 'PUBLISHED',
                          });
                          services.reload();
                        } catch (error) {
                          const described = describeError(error);
                          toast.error(described.title, described.body);
                        }
                      }}
                    >
                      {row.state === 'PUBLISHED' ? 'Wycofaj' : 'Opublikuj'}
                    </Button>
                  ) : null,
              },
            ]}
          />
        )}
      </Card>

      <Modal
        open={open}
        onOpenChange={setOpen}
        title="Nowa usługa"
        footer={
          <>
            <Button onClick={() => setOpen(false)}>Anuluj</Button>
            <Button
              variant="primary"
              disabled={!form.serviceKey || !form.name || !form.summary}
              onClick={async () => {
                try {
                  await api.post('/api/admin/services', {
                    serviceKey: form.serviceKey,
                    name: form.name,
                    summary: form.summary,
                    ...(form.description ? { description: form.description } : {}),
                    sensitivity: form.sensitivity,
                  });
                  toast.success('Usługa utworzona', form.serviceKey);
                  setOpen(false);
                  setForm({
                    serviceKey: '',
                    name: '',
                    summary: '',
                    description: '',
                    sensitivity: 'INTERNAL',
                  });
                  services.reload();
                } catch (error) {
                  const described = describeError(error);
                  toast.error(described.title, described.body);
                }
              }}
            >
              Utwórz
            </Button>
          </>
        }
      >
        <Field
          label="Klucz usługi"
          hint="Małe litery, np. „companies”. Trafia do kluczy operacji: companies.lookup."
        >
          {(id) => (
            <TextInput
              id={id}
              value={form.serviceKey}
              onChange={(event) => setForm({ ...form, serviceKey: event.target.value })}
            />
          )}
        </Field>
        <Field label="Nazwa">
          {(id) => (
            <TextInput
              id={id}
              value={form.name}
              onChange={(event) => setForm({ ...form, name: event.target.value })}
            />
          )}
        </Field>
        <Field label="Streszczenie" hint="Jedno zdanie. To zobaczy agent w wynikach wyszukiwania.">
          {(id) => (
            <TextInput
              id={id}
              value={form.summary}
              onChange={(event) => setForm({ ...form, summary: event.target.value })}
            />
          )}
        </Field>
        <Field label="Opis">
          {(id) => (
            <TextArea
              id={id}
              value={form.description}
              onChange={(event) => setForm({ ...form, description: event.target.value })}
            />
          )}
        </Field>
        <Field label="Klasyfikacja danych">
          {(id) => (
            <Select
              id={id}
              value={form.sensitivity}
              onChange={(event) => setForm({ ...form, sensitivity: event.target.value })}
            >
              <option value="PUBLIC">PUBLIC</option>
              <option value="INTERNAL">INTERNAL</option>
              <option value="CONFIDENTIAL">CONFIDENTIAL</option>
              <option value="RESTRICTED">RESTRICTED</option>
            </Select>
          )}
        </Field>
      </Modal>
    </>
  );
}
